import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, Chip
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon, Group as AgentIcon,
  TrendingUp as TrendIcon, AccessTime as TimeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import supportService from '../../../services/supportService';

const AdminSupportDashboard = () => {
  const { t } = useTranslation(['support', 'common']);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await supportService.getAdminDashboard();
      if (res.success) setStats(res.data);
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const statCards = [
    {
      label: t('support:admin.dashboard.totalTickets'),
      value: stats?.totalTickets || 0,
      icon: <TicketIcon />,
      color: 'primary.main'
    },
    {
      label: t('support:admin.dashboard.openTickets'),
      value: stats?.openTickets || 0,
      icon: <TrendIcon />,
      color: 'warning.main'
    },
    {
      label: t('support:admin.dashboard.avgResponseTime'),
      value: stats?.avgResponseTime ? `${Math.round(stats.avgResponseTime)}min` : '-',
      icon: <TimeIcon />,
      color: 'info.main'
    },
    {
      label: t('support:admin.dashboard.agentsOnline'),
      value: stats?.agentsOnline || 0,
      icon: <AgentIcon />,
      color: 'success.main'
    }
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t('support:admin.dashboard.title')}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: card.color, color: 'white', p: 1.5, borderRadius: 2, display: 'flex' }}>
                  {card.icon}
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700}>{card.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tickets by status */}
      {stats?.byStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('support:admin.dashboard.ticketsByStatus')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(stats.byStatus).map(([status, count]) => (
                <Chip key={status} label={`${t(`support:status.${status}`)}: ${count}`} variant="outlined" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tickets by category */}
      {stats?.byCategory && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('support:admin.dashboard.ticketsByCategory')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(stats.byCategory).map(([cat, count]) => (
                <Chip key={cat} label={`${t(`support:categories.${cat}`, cat)}: ${count}`} variant="outlined" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AdminSupportDashboard;
